<template>
    <section v-if="manufacturer" class="text-base">
        <ManufacturerHeader id="mfr-header" />
        <div class="container flex flex-col sm:flex-row gap-4 sm:gap-8 py-8">
            <div class="shrink-0 sm:w-64 md:w-72 sm:order-last xl:w-80">
                <ManufacturerDetail id="mfr-detail" class="sm:sticky top-0 md:top-20">
                    <ul>
                        <li
                            v-for="section in sections"
                            :key="section.id"
                            class="mb-1.5"
                            :class="{ 'font-bold': section.id == activeId }">
                            <a :href="'#' + section.id" @click="setActive(section.id)"> {{ section.label }} </a>
                        </li>
                    </ul>
                </ManufacturerDetail>
            </div>
            <div class="grow flex flex-col gap-2">
                <ManufacturerSearch id="mfr-search" />
                <ManufacturerDistributors
                    id="mfr-distributors"
                    class="sm:scroll-mt-16"
                    :regions="dataRegions"
                    :sponsors="dataSponsors" />
                <ManufacturerCategories id="mfr-categories" class="sm:scroll-mt-16" :categories="dataCategories" />
                <ManufacturerSeries id="mfr-series" class="sm:scroll-mt-16" :series="dataSeries" />
                <ManufacturerProducts id="mfr-products" class="sm:scroll-mt-16" :parts="dataParts" />
            </div>
        </div>
    </section>
</template>
<script setup lang="ts">
import { useActiveScroll } from "vue-use-active-scroll";

const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const { data: manufacturer, error } = await useApi<ManufacturerDetails>("manufacturer", {
    query: { virtualPath: route.params.company },
});

if (error.value) showError(error.value);

const mfrPath = manufacturer.value?.VirtualPath;
if (mfrPath) await router.replace({ params: { company: mfrPath } });

const title = computed(() => t("Manufacturer.ManufacturerTitle", [manufacturer.value?.Name]));
const description = computed(() => t("Manufacturer.MetaDescriptionFormat", [manufacturer.value?.Name]));

useSeoMeta({ title, ogTitle: title, description, ogDescription: description });

// categories, series, and products are "below the fold" so we can fetch them lazily to speed the initial page load
const fetchOptions = { query: { virtualPath: route.params.company } };
const lazyFetchOptions = { ...fetchOptions, lazy: true, server: false };
const [
    { data: dataSponsors },
    { data: dataRegions },
    { data: dataCategories, status: statusCategories },
    { data: dataSeries, status: statusSeries },
    { data: dataParts, status: statusParts },
] = await Promise.all([
    useApi<ManufacturerSponsor[]>("manufacturer/sponsors", fetchOptions),
    useApi<ManufacturerRegion[]>("manufacturer/distributors", fetchOptions),
    useApi<ManufacturerCategories>("manufacturer/categories", lazyFetchOptions),
    useApi<ProductSeries[]>(`product-series/popular`, lazyFetchOptions),
    useApi<ManufacturerPopularParts>(`manufacturer/popular-parts`, lazyFetchOptions),
]);

const hasRegions = computed(() => Array.isArray(dataRegions.value) && dataRegions.value.length > 0);
const hasCategories = computed(
    () => Array.isArray(dataCategories.value?.Categories) && dataCategories.value.Categories.length > 0
);
const hasSeries = computed(() => Array.isArray(dataSeries.value) && dataSeries.value.length > 0);
const hasTopParts = computed(() => dataParts.value && dataParts.value.TopParts.length > 0);
const hasBottomParts = computed(() => dataParts.value && dataParts.value.BottomParts.length > 0);
const hasAnyParts = computed(() => hasTopParts.value || hasBottomParts.value);

const sections = computed<{ id: string; label: string }[]>(() => {
    const output = [];

    if (hasRegions.value)
        output.push({ id: "mfr-distributors", label: t("Manufacturer.AuthorizedDistributorsHeading") });

    if (statusCategories.value == "idle" || statusCategories.value == "pending" || hasCategories.value)
        output.push({ id: "mfr-categories", label: t("TopSearches.TopCategoriesHeader") });

    if (statusSeries.value == "idle" || statusSeries.value == "pending" || hasSeries.value)
        output.push({ id: "mfr-series", label: t("ProductSeries.PopularProductSeries") });

    if (statusParts.value == "idle" || statusParts.value == "pending" || hasAnyParts.value)
        output.push({ id: "mfr-products", label: t("TopSearches.TopSearchesHeader") });

    return output;
});

const sectionIds = computed(() => sections.value.map((s) => s.id));

const { setActive, activeId } = useActiveScroll(sectionIds, {
    jumpToFirst: false,
    edgeOffset: { first: 0, last: 0 },
    overlayHeight: 64,
});

provide(ManufacturerInjectKey, { manufacturer });
</script>
